"use client";

import { BedDouble, Building2, Users } from "lucide-react";
import { memo } from "react";
import { UseFormReturn } from "react-hook-form";

import { Button } from "@/components/ui/button";

import { IHotelsSearchSchema } from "../../types/hotel-search-type";
import CheckInCheckOutDate from "./hotel-checkin-checkout-date";
import HotelLocationSelect from "./hotel-location-select";
import HotelRoomAndGuests from "./hotel-room-and-guest-select";

interface SummaryFieldProps {
  totalGuests: number;
  totalRooms: number;
}

interface HotelFieldProps {
  form: UseFormReturn<IHotelsSearchSchema>;
  disabled?: boolean;
}

const SUMMARY_BUTTON_CLASS =
  "pointer-events-none h-8 rounded-lg border border-border/60 bg-background/85 px-2 text-xs font-semibold text-foreground shadow-xs md:rounded-md md:border-0 md:bg-primary/10 md:px-4 md:text-sm md:text-primary md:shadow-none";

const SearchType = memo(function SearchType() {
  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <Building2 className="h-4 w-4" />
      Hotels
    </Button>
  );
});

const Guests = memo(function Guests({
  totalGuests,
}: Pick<SummaryFieldProps, "totalGuests">) {
  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <Users className="h-4 w-4" />
      {totalGuests} Guest{totalGuests > 1 ? "s" : ""}
    </Button>
  );
});

const Rooms = memo(function Rooms({
  totalRooms,
}: Pick<SummaryFieldProps, "totalRooms">) {
  return (
    <Button
      type="button"
      variant="ghost"
      className={SUMMARY_BUTTON_CLASS}
    >
      <BedDouble className="h-4 w-4" />
      {totalRooms} Room{totalRooms > 1 ? "s" : ""}
    </Button>
  );
});

const Location = memo(function Location({ form, disabled }: HotelFieldProps) {
  return <HotelLocationSelect form={form} popoverModal disabled={disabled} />;
});

const DateRange = memo(function DateRange({ form }: HotelFieldProps) {
  return <CheckInCheckOutDate form={form} />;
});

const RoomAndGuests = memo(function RoomAndGuests({ form }: HotelFieldProps) {
  return <HotelRoomAndGuests form={form} />;
});

export const HotelFields = {
  SearchType,
  Guests,
  Rooms,
  Location,
  DateRange,
  RoomAndGuests,
};
